import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from '../../../consts/table';
import { Count } from '../../../models/count';
import { Sport } from '../../../models/sport';

const DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER = 'startTime ASC';

export function getTournamentEvents(user: AppUser, tournamentId: string, filter, options: { summary?: boolean } = {}) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      summary: options.summary === true ? true : undefined,
      filter: {
        where: {
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER
      }
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events`, config)
    .then(response => {
      return response.data;
    });
}

export function getAllTournamentEvents(user: AppUser, tournamentId: string, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER
        }
      }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events`, config)
    .then(response => {
      return response.data;
    });
}

export function getTournamentEventsCount(user: AppUser, tournamentId: string, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events/count`, config)
    .then(response => {
      return response.data;
    });
}

export function getTournamentEventsBulkManagement(user: AppUser, tournamentId: string, filter, now: string) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events/bulk-management`, {
      headers: { usid: key },
      params: {
        now,
        filter: {
          where: { ...filter.where },
          skip: filter.skip,
          limit: filter.limit,
          order: filter.order || DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER
        }
      }
    })
    .then(response => response.data);
}

export function getTournamentEventIds(user: AppUser, tournamentId: string, where: any): Promise<string[]> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events/bulk-management`, {
    headers: { usid: key },
    params: { idsOnly: true, filter: { where, skip: 0, limit: 1 } }
  }).then(response => response.data.ids || []);
}

export function getTournamentEventSports(user: AppUser, tournamentId: string): Promise<Sport[]> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/sports`, {
      headers: { usid: key },
      params: { filter: { limit: DEFAULT_LIMIT, skip: DEFAULT_SKIP } }
    })
    .then(response => response.data);
}

export function updateTournamentEventsBulk(user: AppUser, tournamentId: string, updates: any[], scheduleOnly = false) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .put(
      `${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events/bulk`,
      scheduleOnly ? { updates, scheduleOnly } : { updates },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function assignTournamentEventNumbers(user: AppUser, tournamentId: string, startFrom: number) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/events/assignQuantumNumbers`,
      { startFrom }, { headers: { usid: key } })
    .then(response => response.data);
}

export function assignPitchByTournamentIdAndEventId(user: AppUser, data, tournamentId, eventId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/events/${eventId}/pitches`,
      data,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function unassignPitchByTournamentIdAndEventId(user: AppUser, tournamentId, eventId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(
      `${window.apiBase}/i/schools/${activeSchoolId}/tournaments/${tournamentId}/events/${eventId}/pitches`,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function deleteEvent(user: AppUser, eventId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.delete(`${window.apiBase}/i/schools/${activeSchoolId}/events/${eventId}`, config).then(response => {
    return response.data;
  });
}

export function getHighLevelTournamentEvents(user: AppUser, filter) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...filter.where
        },
        limit: filter.limit,
        order: filter.order || DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/events/`, config).then(response => {
    return response.data;
  });
}

export function getAllSchoolUnionHighLevelTournamentEvents(user: AppUser, tournamentId: string, where?) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER
        }
      }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events`, config)
    .then(response => {
      return response.data;
    });
}

export function getSchoolUnionHighLevelTournamentEvents(user: AppUser, tournamentId: string, filter) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...filter.where
        },
        limit: filter.limit,
        order: filter.order || DEFAULT_SCHOOL_TOURNAMENT_EVENTS_ORDER,
        skip: filter.skip
      }
    }
  };

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events`, config)
    .then(response => {
      return response.data;
    });
}

export function getSchoolUnionHighLevelTournamentEventsCount(user: AppUser, tournamentId: string, where) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/schools/tournaments/${tournamentId}/events/count`, config)
    .then(response => {
      return response.data;
    });
}
